const API_ENDPOINT = import.meta.env.VITE_APP_REST_API_ENDPOINT as string;
const API_REGEXP = /^(https?):\/\/(.+)$/;
const [, API_SCHEMA, API_HOST] = API_REGEXP.exec(API_ENDPOINT)!;
const TOKEN_URL = `${API_ENDPOINT}/oauth/v2/token`;

export const API_V1_SWAGGER_SPEC = {
    swagger: "2.0",
    host: API_HOST,
    schemes: [API_SCHEMA],
    info: {
        title: "IrriWatch API V1",
        description:
            "\u003Cul\u003E \u003Cli\u003EAccess to the API is granted for every user by accessing their personal API credentials under their account name.\u003C/li\u003E \u003Cli\u003EAccess keys are secrets and \u003Cstrong\u003EMUST NOT\u003C/strong\u003E be shared with third parties nor be shared via email. If your access keys have been misused, you should generate new credentials.\u003C/li\u003E \u003Cli\u003ECalls to the API may be blocked when many calls are being performed in a given moment.\u003C/li\u003E \u003Cli\u003EResources are generally only 7 days available for download after generation\u003C/li\u003E \u003Cli\u003ESoftware using the API must be able to follow redirects (http code 302)\u003C/li\u003E \u003Cli\u003E\u003Cstrong\u003E\u003Cspan style='color:red'\u003EPlease note all calls to this API are logged. Abuse of the API may result in removal of access rights\u003C/span\u003E\u003C/strong\u003E\u003C/li\u003E \u003C/ul\u003E",
        version: "-",
    },
    paths: {
        "/api/v1/company": {
            get: {
                responses: {
                    "200": {
                        description: "List all companies",
                        schema: {
                            items: {
                                $ref: "#/definitions/CompanyResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Returns the details of a company",
                        schema: {
                            $ref: "#/definitions/CompanyResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/user": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "List all users",
                        schema: {
                            items: {
                                $ref: "#/definitions/UserResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order": {
            get: {
                parameters: [
                    {
                        name: "active",
                        in: "query",
                        required: false,
                        description:
                            "Returns inactive orders if false, active orders if true, all orders if not provided",
                        type: "boolean",
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Lists all orders for a company",
                        schema: {
                            items: {
                                $ref: "#/definitions/OrderResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
            post: {
                parameters: [
                    {
                        name: "order",
                        in: "body",
                        description: "Create new order",
                        schema: {
                            $ref: "#/definitions/OrderRequest",
                        },
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Returns the order created",
                        schema: {
                            $ref: "#/definitions/OrderResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/complete": {
            post: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Empty response",
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Detailed order status",
                        schema: {
                            $ref: "#/definitions/OrderResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/meteo": {
            post: {
                parameters: [
                    {
                        name: "meteo_record",
                        in: "body",
                        required: true,
                        description:
                            "Create new meteo record. NOTE! Time zones are ignored. Period is interpreted as UTC",
                        schema: {
                            $ref: "#/definitions/ClimateRequest",
                        },
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Returns the meteo record created",
                        schema: {
                            $ref: "#/definitions/ClimateResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result/date/{date}": {
            get: {
                summary: "Retrieve order results as a ZIP file",
                description: "Returns a ZIP file containing order results for a specific company, order, and date.",
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                            format: "uuid",
                        },
                        description: "UUID of the company",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                            format: "uuid",
                        },
                        description: "UUID of the order",
                    },
                    {
                        name: "date",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                            format: "date",
                        },
                        description: "Date in YYYY-MM-DD format",
                    },
                ],
                responses: {
                    "200": {
                        description: "Returns a ZIP file containing order results",
                        content: {
                            "application/zip": {
                                schema: {
                                    type: "string",
                                    format: "binary",
                                },
                            },
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Lists all results ZIP for an order",
                        schema: {
                            items: {
                                $ref: "#/definitions/ResultResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result/{resultUuid}": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "resultUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Download result ZIP",
                        schema: {
                            type: "string",
                            format: "binary",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result/{date}/field_level": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "date",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description:
                            "Download JSON field level parameter result, date must follow format YYYYMMDD, e.g. 5 februari 2021 is 20210205",
                        schema: {
                            type: "string",
                            format: "binary",
                        },
                    },
                },
            },
        },
        "/api/v1/reference/crop": {
            get: {
                responses: {
                    "200": {
                        description: "Crop coding list",
                    },
                },
            },
        },
        "/api/v1/reference/irrigation": {
            get: {
                responses: {
                    "200": {
                        description: "Irrigation coding list",
                    },
                },
            },
        },
        "/api/v1/reference/soil": {
            get: {
                responses: {
                    "200": {
                        description: "Soil coding list",
                    },
                },
            },
        },
    },
    definitions: {
        WindResponse: {
            required: ["average_speed"],
            properties: {
                average_speed: {
                    type: "number",
                    format: "float",
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        WindRequest: {
            required: ["average_speed"],
            properties: {
                average_speed: {
                    type: "number",
                    format: "float",
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        UserResponse: {
            required: ["first_name", "last_name", "email"],
            properties: {
                first_name: {
                    type: "string",
                    maxLength: "200",
                },
                last_name: {
                    type: "string",
                    maxLength: "200",
                },
                email: {
                    type: "string",
                    maxLength: "200",
                },
                uuid: {type: "string"},
            },
            type: "object",
        },
        TemperatureResponse: {
            required: ["average"],
            properties: {
                min: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                max: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                average: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        TemperatureRequest: {
            required: ["average"],
            properties: {
                min: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                max: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                average: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        ResultResponse: {
            required: ["uuid", "name", "date"],
            properties: {
                uuid: {
                    type: "string",
                },
                name: {
                    type: "string",
                    maxLength: "200",
                },
                date: {
                    type: "string",
                },
            },
            type: "object",
        },
        ReferenceET0Response: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        ReferenceET0Request: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        PrecipitationResponse: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        PrecipitationRequest: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        PeriodResponse: {
            required: ["start", "end"],
            properties: {
                start: {
                    type: "string",
                    format: "date-time",
                },
                end: {
                    type: "string",
                    format: "date-time",
                },
            },
            type: "object",
        },
        PeriodRequest: {
            required: ["start", "end"],
            properties: {
                start: {
                    type: "string",
                    format: "date-time",
                },
                end: {
                    type: "string",
                    format: "date-time",
                },
            },
            type: "object",
        },
        OrderRequest: {
            required: ["fields"],
            properties: {
                fields: {
                    additionalProperties: true,
                    type: "object",
                },
                subscription_type: {
                    type: "string",
                    maxLength: "200",
                },
            },
            type: "object",
        },
        OrderResponse: {
            required: ["fields"],
            properties: {
                uuid: {
                    type: "string",
                },
                id: {
                    type: "integer",
                },
                fields: {
                    additionalProperties: true,
                    type: "object",
                },
                state: {
                    type: "string",
                },
                subscription_type: {
                    type: "string",
                    maxLength: "200",
                },
            },
            type: "object",
        },
        LocationResponse: {
            required: ["lat", "lon"],
            properties: {
                lat: {
                    type: "number",
                    format: "float",
                },
                lon: {
                    type: "number",
                    format: "float",
                },
            },
            type: "object",
        },
        LocationRequest: {
            required: ["lat", "lon"],
            properties: {
                lat: {
                    type: "number",
                    format: "float",
                },
                lon: {type: "number", format: "float"},
            },
            type: "object",
        },
        HumidityResponse: {
            required: ["relative_average_in_percentage"],
            properties: {
                relative_average_in_percentage: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
                height: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
            },
            type: "object",
        },
        HumidityRequest: {
            required: ["relative_average_in_percentage"],
            properties: {
                relative_average_in_percentage: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
                height: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
            },
            type: "object",
        },
        CompanyResponse: {
            required: ["name"],
            properties: {
                name: {
                    type: "string",
                    maxLength: "200",
                },
                uuid: {
                    type: "string",
                },
            },
            type: "object",
        },
        ClimateResponse: {
            required: ["period", "location"],
            properties: {
                reference_et0: {$ref: "#/definitions/ReferenceET0Response"},
                period: {
                    $ref: "#/definitions/PeriodResponse",
                },
                precipitation: {
                    $ref: "#/definitions/PrecipitationResponse",
                },
                temperature: {
                    $ref: "#/definitions/TemperatureResponse",
                },
                humidity: {
                    $ref: "#/definitions/HumidityResponse",
                },
                wind: {
                    $ref: "#/definitions/WindResponse",
                },
                location: {
                    $ref: "#/definitions/LocationResponse",
                },
            },
            type: "object",
        },
        ClimateRequest: {
            required: ["period", "location"],
            properties: {
                reference_et0: {
                    $ref: "#/definitions/ReferenceET0Request",
                },
                period: {
                    $ref: "#/definitions/PeriodRequest",
                },
                precipitation: {
                    $ref: "#/definitions/PrecipitationRequest",
                },
                temperature: {
                    $ref: "#/definitions/TemperatureRequest",
                },
                humidity: {
                    $ref: "#/definitions/HumidityRequest",
                },
                wind: {
                    $ref: "#/definitions/WindRequest",
                },
                location: {
                    $ref: "#/definitions/LocationRequest",
                },
            },
            type: "object",
        },
        AreaDTO: [],
        AreaResultDTO: [],
        TemperatureDTO: {
            required: ["average"],
            properties: {
                min: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                max: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                average: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        HumidityDTO: {
            required: ["relative_average_in_percentage"],
            properties: {
                relative_average_in_percentage: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
                height: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
            },
            type: "object",
        },
        WindDTO: {
            required: ["average_speed"],
            properties: {
                average_speed: {
                    type: "number",
                    format: "float",
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
    },
    securityDefinitions: {
        "Client Credentials": {
            name: "Authorization",
            type: "oauth2",
            flow: "application",
            tokenUrl: TOKEN_URL,
            description: "Provides an application flow access_token",
        },
    },
    security: [
        {
            "Client Credentials": [],
        },
    ],
};

export const API_V2_SWAGGER_SPEC = {
    swagger: "2.0",
    host: API_HOST,
    schemes: [API_SCHEMA],
    info: {
        title: "IrriWatch API V2",
        description:
            "Access to the API is granted for every user by accessing their personal API credentials under their account name",

        version: "-",
    },
    paths: {
        "/api/v2/company/{companyId}/madera-reports/download": {
            get: {
                summary: "Download a Madera report",
                parameters: [
                    {
                        name: "companyId",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                        },
                        description: "ID of the company",
                    },
                    {
                        name: "parcel",
                        in: "query",
                        schema: {
                            type: "string",
                        },
                    },
                    {
                        name: "startDate",
                        in: "query",
                        schema: {
                            type: "string",
                            format: "date",
                        },
                    },
                    {
                        name: "endDate",
                        in: "query",
                        schema: {
                            type: "string",
                            format: "date",
                        },
                    },
                    {
                        name: "reportType",
                        in: "query",
                        schema: {
                            type: "string",
                            enum: ["PARCEL", "FARM_UNIT"],
                        },
                    },
                    {
                        name: "farmUnit",
                        in: "query",
                        schema: {
                            type: "string",
                        },
                    },
                    {
                        name: "accountId",
                        in: "query",
                        required: false,
                        schema: {
                            type: "string",
                        },
                    },
                ],
                responses: {
                    "200": {
                        description: "Signed URL for downloading the report",
                        content: {
                            "application/json": {
                                schema: {
                                    type: "object",
                                    properties: {
                                        url: {
                                            type: "string",
                                            format: "uri",
                                        },
                                    },
                                },
                            },
                        },
                    },
                    "400": {
                        description: "Bad request",
                    },
                    "404": {
                        description: "Report not found",
                    },
                },
            },
        },
        "/api/v2/company/{companyId}/madera-reports": {
            post: {
                summary: "Upload Madera reports",
                consumes: ["multipart/form-data"],
                parameters: [
                    {
                        name: "companyId",
                        in: "path",
                        required: true,
                        type: "string",
                        description: "ID of the company",
                    },
                    {
                        name: "attachments",
                        in: "formData",
                        description: "File to upload",
                        required: true,
                        type: "file",
                    },
                ],
                responses: {
                    "200": {
                        description: "Upload results",
                        schema: {
                            type: "object",
                            properties: {
                                results: {
                                    type: "array",
                                    items: {
                                        type: "object",
                                        properties: {
                                            fileName: {type: "string"},
                                            status: {type: "string"},
                                        },
                                    },
                                },
                            },
                        },
                    },
                    "400": {
                        description: "Bad request",
                    },
                },
            },
        },
    },
    securityDefinitions: {
        "Client Credentials": {
            name: "Authorization",
            type: "oauth2",
            flow: "application",
            tokenUrl: TOKEN_URL,
            description: "Provides an application flow access_token",
        },
    },
    security: [
        {
            "Client Credentials": [],
        },
    ],
};
